export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="privacy-page">
      <p className="overline mb-3">Legal</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight mb-3">Privacy Policy</h1>
      <p className="text-xs text-[#8C857B] mb-10">Last updated: February 2026</p>

      <div className="prose prose-neutral max-w-none space-y-8 text-[#3a3733] text-sm md:text-base leading-relaxed">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">1. Introduction</h2>
          <p>
            Welcome to Sanyog Vivah ("Sanyog", "we", "our"). We operate the matrimony platform
            at <a href="https://sanyogvivah.in" className="text-[#C85A44] hover:underline">sanyogvivah.in</a>.
            This Privacy Policy explains how we collect, use, protect, and share your personal
            information when you use our service. This policy is aligned with India's Digital
            Personal Data Protection Act, 2023 (DPDP Act).
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">2. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account data:</strong> name, email, password (encrypted), gender, date of birth.</li>
            <li><strong>Profile data:</strong> religion, community, mother tongue, city, education, profession,
              income, marital status, photos, family details, partner preferences.</li>
            <li><strong>Usage data:</strong> pages visited, interests sent/received, matches, messages exchanged.</li>
            <li><strong>Payment data:</strong> transaction records via Stripe / Razorpay. We do NOT store your card details.</li>
            <li><strong>Device data:</strong> browser type, IP address (for security), language preference.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To create and manage your matrimonial profile.</li>
            <li>To show compatible profiles to potential matches (opposite gender only).</li>
            <li>To enable mutual matches and private conversations.</li>
            <li>To process Premium subscription payments.</li>
            <li>To generate AI compatibility scores (via Google Gemini, aggregated only).</li>
            <li>To send transactional notifications (interest received, mutual match).</li>
            <li>To improve the service and prevent fraud/spam.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">4. Who Can See Your Data</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Other users:</strong> only see your public profile (name, photos, bio, city, religion, education, profession).
              Your email and phone are NEVER shown to other users.</li>
            <li><strong>Mutual matches:</strong> can chat with you inside the app.</li>
            <li><strong>Sanyog admins:</strong> for moderation, verification, and support only.</li>
            <li><strong>Payment processors:</strong> Stripe / Razorpay receive only what's needed to process your payment.</li>
            <li><strong>AI processing:</strong> profile text is sent to Google Gemini for compatibility scoring — no photos, no contact info.</li>
          </ul>
          <p className="mt-3"><strong>We never sell your data to third parties.</strong></p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">5. Data Security</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Passwords hashed with bcrypt — even we can't read them.</li>
            <li>HTTPS/SSL encryption for all data in transit.</li>
            <li>Login sessions use httpOnly cookies (protected against theft).</li>
            <li>Access to admin functions restricted and audited.</li>
            <li>Regular database backups.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">6. Your Rights (DPDP Act)</h2>
          <p>As per the DPDP Act 2023, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li><strong>Access</strong> your personal data.</li>
            <li><strong>Correct</strong> inaccurate information (via profile edit).</li>
            <li><strong>Erase</strong> your account and all associated data.</li>
            <li><strong>Withdraw consent</strong> at any time.</li>
            <li><strong>Complain</strong> to the Data Protection Board of India.</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, email us at <strong>support@sanyogvivah.in</strong>.
            We respond within 30 days.
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">7. Cookies</h2>
          <p>
            We use only essential cookies for authentication (login) and language preference.
            No advertising or third-party tracking cookies.
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">8. Children</h2>
          <p>
            Sanyog Vivah is only for adults aged 18+ (or legal age of marriage in your state).
            We do not knowingly collect data from minors. If you believe a minor has registered,
            report immediately via the in-app Report feature.
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">9. Data Retention</h2>
          <p>
            We keep your data as long as your account is active. If you delete your account,
            all personal data is permanently removed within 30 days (some records may be retained
            for legal/tax purposes for up to 7 years, in anonymized form).
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">10. Contact</h2>
          <p>
            Grievance Officer: <strong>Sanyog Vivah Admin</strong><br/>
            Email: <a href="mailto:support@sanyogvivah.in" className="text-[#C85A44] hover:underline">support@sanyogvivah.in</a><br/>
            Website: <a href="https://sanyogvivah.in" className="text-[#C85A44] hover:underline">sanyogvivah.in</a>
          </p>
        </section>

        <section>
          <h2 className="font-serif-display text-2xl mb-3">11. Changes to This Policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be notified to you
            via in-app banner or email. Continued use of Sanyog after changes constitutes acceptance.
          </p>
        </section>
      </div>
    </div>
  );
}
